import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import "./RoadmapCreate.css";

import SkillModal   from "../../components/SkillModal/SkillModal";
import ProjectModal from "../../components/ProjectModal/ProjectModal";

/* ── helpers ──────────────────────────────────────────────── */
const STATUS_META = {
  planned:     { icon: "📋", label: "Planned",     color: "#64748b" },
  in_progress: { icon: "⚡", label: "In Progress", color: "#f59e0b" },
  completed:   { icon: "✅", label: "Completed",   color: "#059669" },
};

const extractVideoId = (url = "") => {
  const m = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return m ? m[1] : null;
};

// ✅ Checks item.thumbnail first (set by existing course path),
//    then falls back to extracting from resource URLs,
//    then checks resource-level thumbnail
const getThumb = (item) => {
  if (item?.thumbnail) return item.thumbnail;
  const sources = item?.resources || item?.playlists || [];
  for (const r of sources) {
    const vid = extractVideoId(r?.url || "");
    if (vid) return `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
    if (r?.thumbnail) return r.thumbnail;
  }
  return null;
};

// ✅ Counts existing_course entries even when url is empty
const getResourceCount = (item) => {
  const sources = item?.resources || item?.playlists || [];
  return sources.filter((r) => r?.url?.trim() || r?.type === "existing_course" || r?.courseId).length;
};

/* ── Component ───────────────────────────────────────────── */
function RoadmapCreate() {
  const navigate = useNavigate();

  const [roadmap,       setRoadmap]       = useState(null);
  const [items,         setItems]         = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [saved,         setSaved]         = useState(false);
  const [draftId,       setDraftId]       = useState(null);
  const [hasPastDraft,  setHasPastDraft]  = useState(false);
  const [publishing,    setPublishing]    = useState(false);

  const [reorderMode,  setReorderMode]  = useState(false);
  const [viewItem,     setViewItem]     = useState(null);

  const [showSkillModal,   setShowSkillModal]   = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingItem,      setEditingItem]      = useState(null);

  const canPublish = items.length >= 2;

  /* ── Init draft ─────────────────────────────────────────── */
  useEffect(() => {
    const init = async () => {
      try {
        const draftRes = await api.get("/roadmap/draft");
        let id;
        if (draftRes.data.draft) {
          id = draftRes.data.roadmap._id;
          setHasPastDraft(true);
        } else {
          const createRes = await api.post("/roadmap/draft");
          id = createRes.data.roadmap._id;
        }
        setDraftId(id);
        const res = await api.get(`/roadmap/${id}`);
        setRoadmap(res.data.roadmap);
        setItems(res.data.roadmap?.items || []);
      } catch {
        navigate("/dashboard");
      }
    };
    init();
  }, [navigate]);

  /* ── Autosave ───────────────────────────────────────────── */
  const autoSave = useCallback(async () => {
    if (!roadmap || !draftId) return;
    try {
      setSaving(true);
      setSaved(false);
      await api.put(`/roadmap/${draftId}`, {
        title:       roadmap.title,
        description: roadmap.description,
        items,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* silent */ }
    finally { setSaving(false); }
  }, [draftId, roadmap, items]);

  useEffect(() => {
    if (!roadmap) return;
    const t = setTimeout(autoSave, 1200);
    return () => clearTimeout(t);
  }, [items, roadmap, autoSave]);

  /* ── Delete item ────────────────────────────────────────── */
  const handleDelete = (order) => {
    setItems((prev) =>
      prev
        .filter((i) => i.order !== order)
        .map((i, idx) => ({ ...i, order: idx + 1 }))
    );
  };

  /* ── Reorder ────────────────────────────────────────────── */
  const move = (order, dir) => {
    setItems((prev) => {
      const arr  = [...prev].sort((a, b) => a.order - b.order);
      const idx  = arr.findIndex((i) => i.order === order);
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return prev;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr.map((i, n) => ({ ...i, order: n + 1 }));
    });
  };

  /* ── Edit item ──────────────────────────────────────────── */
  const handleEditSave = (updatedData) => {
    setItems((prev) =>
      prev.map((i) =>
        i.order === editingItem.order ? { ...i, ...updatedData } : i
      )
    );
    setEditingItem(null);
  };

  /* ── Publish ────────────────────────────────────────────── */
  const handlePublish = async () => {
    if (!canPublish || !draftId) return;
    setPublishing(true);
    try {
      await api.put(`/roadmap/${draftId}`, {
        title:       roadmap.title,
        description: roadmap.description,
        items,
      });
      await api.patch(`/roadmap/${draftId}/publish`);
      navigate("/roadmaps");
    } catch (err) {
      console.error(err);
    } finally {
      setPublishing(false);
    }
  };

  /* ── Save draft ─────────────────────────────────────────── */
  const handleSaveDraft = async () => {
    if (!draftId) return;
    setSaving(true);
    try {
      await api.put(`/roadmap/${draftId}`, {
        title:       roadmap.title,
        description: roadmap.description,
        items,
      });
      navigate("/roadmaps");
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!roadmap) {
    return (
      <div className="rc-loading">
        <div className="rc-spinner" />
        <p>Loading…</p>
      </div>
    );
  }

  const sorted = [...items].sort((a, b) => a.order - b.order);

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="rc-page">
      <div className="rc-container">

        {/* ── Header ───────────────────────────────────────── */}
        <div className="rc-header">
          <div className="rc-header-text">
            <h1>Build Your Roadmap</h1>
            <p>Plan your learning journey step by step</p>
          </div>
          <div className="rc-header-status">
            {saving && <span className="rc-save-pill">Saving…</span>}
            {saved  && <span className="rc-save-pill rc-save-pill--saved">✓ Saved</span>}
          </div>
        </div>

        {hasPastDraft && (
          <div className="rc-draft-banner">
            📝 You have a saved draft from an earlier session — picking up where you left off.
          </div>
        )}

        {/* ── Meta inputs ──────────────────────────────────── */}
        <div className="rc-meta-card">
          <div className="rc-field">
            <label className="rc-label">Roadmap Title</label>
            <input
              className="rc-input"
              placeholder="e.g. Full-Stack Developer Path"
              value={roadmap.title === "Untitled Roadmap" ? "" : roadmap.title}
              onChange={(e) => setRoadmap({ ...roadmap, title: e.target.value || "Untitled Roadmap" })}
            />
          </div>
          <div className="rc-field">
            <label className="rc-label">Description</label>
            <textarea
              className="rc-textarea"
              placeholder="What is this roadmap for? What's your goal?"
              value={roadmap.description || ""}
              onChange={(e) => setRoadmap({ ...roadmap, description: e.target.value })}
              rows={2}
            />
          </div>
        </div>

        {/* ── Timeline ─────────────────────────────────────── */}
        <div className="rc-section">
          <div className="rc-section-header">
            <div>
              <h2>Timeline</h2>
              <span className="rc-section-sub">{items.length} item{items.length !== 1 ? "s" : ""}</span>
            </div>
            {items.length > 1 && (
              <button
                className={`rc-reorder-btn ${reorderMode ? "rc-reorder-btn--active" : ""}`}
                onClick={() => setReorderMode((v) => !v)}
              >
                {reorderMode ? "✓ Done Reordering" : "↕ Reorder"}
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="rc-empty">
              <span className="rc-empty-icon">🗺️</span>
              <p>No items yet — add a skill or project below to begin.</p>
            </div>
          ) : (
            <div className="rc-timeline">
              {sorted.map((item, i) => {
                const thumb          = getThumb(item);
                const resCount       = getResourceCount(item);
                const fromCourse     = !!(item.existingCourseId || (item.resources || []).some(r => r?.type === "existing_course"));
                const status         = item.type === "project"
                  ? (STATUS_META[item.status] || STATUS_META.planned)
                  : null;

                return (
                  <div
                    key={item._id || item.order}
                    className={`rc-item rc-item--${item.type}`}
                    style={{ animationDelay: `${i * 0.04}s` }}
                  >
                    <div className="rc-step-num">{i + 1}</div>
                    {i < sorted.length - 1 && <div className="rc-connector" />}

                    <div
                      className="rc-item-card"
                      onClick={() => !reorderMode && setViewItem(item)}
                    >
                      {thumb && (
                        <img
                          src={thumb}
                          alt=""
                          className="rc-item-thumb"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}

                      <div className="rc-item-body">
                        <div className="rc-item-top">
                          <span className={`rc-type-chip rc-type-chip--${item.type}`}>
                            {item.type === "skill" ? "🎯 Skill" : "🚀 Project"}
                          </span>
                          {status && (
                            <span
                              className="rc-status-dot"
                              style={{ background: status.color }}
                              title={status.label}
                            />
                          )}
                        </div>
                        <div className="rc-item-name">{item.name}</div>

                        {item.type === "project" && Array.isArray(item.techStack) && item.techStack.length > 0 && (
                          <div className="rc-tech-row">
                            {item.techStack.slice(0, 3).map((t) => (
                              <span key={t} className="rc-tech-chip">{t}</span>
                            ))}
                            {item.techStack.length > 3 && (
                              <span className="rc-tech-chip rc-tech-chip--more">
                                +{item.techStack.length - 3}
                              </span>
                            )}
                          </div>
                        )}

                        {/* ✅ Resource count — existing course shows "1 course (N videos)" */}
                        {item.type === "skill" && (
                          <span className="rc-resource-count">
                            {fromCourse
                              ? `📚 1 course${item.totalVideos ? ` (${item.totalVideos} videos)` : ""}`
                              : `📚 ${resCount} resource${resCount !== 1 ? "s" : ""}`}
                          </span>
                        )}
                      </div>

                      <div className="rc-item-actions">
                        {reorderMode ? (
                          <>
                            <button
                              className="rc-move-btn"
                              onClick={(e) => { e.stopPropagation(); move(item.order, -1); }}
                              disabled={i === 0}
                            >↑</button>
                            <button
                              className="rc-move-btn"
                              onClick={(e) => { e.stopPropagation(); move(item.order, 1); }}
                              disabled={i === sorted.length - 1}
                            >↓</button>
                          </>
                        ) : (
                          <button
                            className="rc-edit-btn"
                            onClick={(e) => { e.stopPropagation(); setEditingItem(item); }}
                            title="Edit"
                          >✏</button>
                        )}
                        <button
                          className="rc-delete-btn"
                          onClick={(e) => { e.stopPropagation(); handleDelete(item.order); }}
                          title="Remove"
                        >✕</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Add actions ──────────────────────────────────── */}
        <div className="rc-add-row">
          <button className="rc-add-btn rc-add-btn--skill" onClick={() => setShowSkillModal(true)}>
            🎯 Add Skill
          </button>
          <button className="rc-add-btn rc-add-btn--project" onClick={() => setShowProjectModal(true)}>
            🚀 Add Project
          </button>
        </div>

        {/* ── Footer actions ───────────────────────────────── */}
        <div className="rc-footer">
          {!canPublish && items.length > 0 && (
            <p className="rc-publish-hint">Add at least 2 items to publish.</p>
          )}
          <div className="rc-footer-btns">
            <button className="rc-btn rc-btn--ghost" onClick={handleSaveDraft} disabled={saving}>
              {saving ? "Saving…" : "Save Draft"}
            </button>
            <button
              className="rc-btn rc-btn--publish"
              onClick={handlePublish}
              disabled={!canPublish || publishing}
            >
              {publishing ? "Publishing…" : "Publish Roadmap 🚀"}
            </button>
          </div>
        </div>

      </div>

      {/* ── View Item Modal ───────────────────────────────── */}
      {viewItem && (
        <>
          <div className="modal-backdrop" onClick={() => setViewItem(null)} />
          <div className="rc-modal-wrapper">
            <ViewItemModal
              item={viewItem}
              onClose={() => setViewItem(null)}
              onEdit={() => { setEditingItem(viewItem); setViewItem(null); }}
            />
          </div>
        </>
      )}

      {/* ── Edit Skill Modal ─────────────────────────────── */}
      {editingItem?.type === "skill" && (
        <>
          <div className="modal-backdrop" onClick={() => setEditingItem(null)} />
          <div className="rc-modal-wrapper">
            <SkillModal
              existing={editingItem}
              onClose={() => setEditingItem(null)}
              onSave={handleEditSave}
            />
          </div>
        </>
      )}

      {/* ── Edit Project Modal ───────────────────────────── */}
      {editingItem?.type === "project" && (
        <>
          <div className="modal-backdrop" onClick={() => setEditingItem(null)} />
          <div className="rc-modal-wrapper">
            <ProjectModal
              existing={editingItem}
              onClose={() => setEditingItem(null)}
              onSave={handleEditSave}
            />
          </div>
        </>
      )}

      {/* ── Add Skill Modal ──────────────────────────────── */}
      {showSkillModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowSkillModal(false)} />
          <div className="rc-modal-wrapper">
            <SkillModal
              onClose={() => setShowSkillModal(false)}
              onSave={(item) => {
                setItems((prev) => [...prev, { ...item, order: prev.length + 1 }]);
                setShowSkillModal(false);
              }}
            />
          </div>
        </>
      )}

      {/* ── Add Project Modal ────────────────────────────── */}
      {showProjectModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowProjectModal(false)} />
          <div className="rc-modal-wrapper">
            <ProjectModal
              onClose={() => setShowProjectModal(false)}
              onSave={(item) => {
                setItems((prev) => [...prev, { ...item, order: prev.length + 1 }]);
                setShowProjectModal(false);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

/* ── ViewItemModal ───────────────────────────────────────── */
function ViewItemModal({ item, onClose, onEdit }) {
  const status = item.type === "project"
    ? (STATUS_META[item.status] || STATUS_META.planned)
    : null;
  const thumb          = getThumb(item);
  const fromCourse     = !!(item.existingCourseId || (item.resources || []).some(r => r?.type === "existing_course"));

  return (
    <div className="vim-modal">
      <div className="vim-header">
        <span className={`vim-type-badge vim-type-badge--${item.type}`}>
          {item.type === "skill" ? "🎯 Skill" : "🚀 Project"}
        </span>
        <div className="vim-header-btns">
          <button className="vim-edit-btn" onClick={onEdit} title="Edit">✏</button>
          <button className="vim-close-btn" onClick={onClose}>✕</button>
        </div>
      </div>

      {thumb && (
        <img
          src={thumb}
          alt=""
          className="vim-thumb"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      )}
      <h3 className="vim-title">{item.name}</h3>

      {/* ── Project details ─────────────────────────────── */}
      {item.type === "project" && (
        <>
          {item.description && <p className="vim-description">{item.description}</p>}
          {status && (
            <div className="vim-row">
              <span className="vim-label">Status</span>
              <span
                className="vim-status-chip"
                style={{ color: status.color, borderColor: status.color + "30", background: status.color + "10" }}
              >
                {status.icon} {status.label}
              </span>
            </div>
          )}
          {Array.isArray(item.techStack) && item.techStack.length > 0 && (
            <div className="vim-row vim-row--col">
              <span className="vim-label">Tech Stack</span>
              <div className="vim-tech-row">
                {item.techStack.map((t) => (
                  <span key={t} className="vim-tech-chip">{t}</span>
                ))}
              </div>
            </div>
          )}
          {(item.githubUrl || item.liveUrl) && (
            <div className="vim-links">
              {item.githubUrl && (
                <a
                  href={item.githubUrl.startsWith("http") ? item.githubUrl : `https://${item.githubUrl}`}
                  target="_blank" rel="noreferrer"
                  className="vim-link vim-link--github"
                >
                  ⚙ GitHub
                </a>
              )}
              {item.liveUrl && (
                <a
                  href={item.liveUrl.startsWith("http") ? item.liveUrl : `https://${item.liveUrl}`}
                  target="_blank" rel="noreferrer"
                  className="vim-link vim-link--live"
                >
                  🌐 Live
                </a>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Skill details ───────────────────────────────── */}
      {item.type === "skill" && (
        <div className="vim-resources">
          <span className="vim-label">Resources</span>

          {/* ✅ Existing course — show thumbnail + label instead of broken empty link */}
          {fromCourse ? (
            <div className="vim-existing-course">
              {thumb && (
                <img
                  src={thumb}
                  alt=""
                  className="vim-existing-thumb"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              <span className="vim-existing-label">
                📚 Linked from My Courses
                {item.totalVideos ? ` · ${item.totalVideos} videos` : ""}
              </span>
            </div>
          ) : (
            <>
              {(item.resources || item.playlists || [])
                .filter((r) => r?.url)
                .map((r, i) => (
                  <a
                    key={i}
                    href={r.url.startsWith("http") ? r.url : `https://${r.url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="vim-resource-link"
                  >
                    {r.type === "video" ? "🎬" : "📋"} {r.url}
                  </a>
                ))}
              {(item.resources || item.playlists || []).filter((r) => r?.url).length === 0 && (
                <span className="vim-empty-resources">No resources added yet.</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RoadmapCreate;